/**
 * Typed fetch client for the loggob API.
 *
 * All requests are cookie-authed (`loggob_token`, HttpOnly) via
 * `credentials: 'include'`. A 401 surfaces as an `ApiError` with
 * `unauthorized = true` so the app can drop back to the login view.
 */

export interface LogRow {
  id: number;
  ts: number;
  container: string;
  stack: string | null;
  service: string | null;
  stream: 'stdout' | 'stderr';
  level: string | null;
  message: string;
  attrs: string | null;
}

export interface QueryResult {
  logs: LogRow[];
  hasMore: boolean;
  /** Present when the server's scan budget stopped a text/attr search before finding `limit`
   * matches: pass back as `scanBeforeId` to continue the scan exactly where it stopped. */
  scanCursor?: number;
  /** Oldest log ts the stopped scan examined ("searched back to ..."). */
  scannedToTs?: number;
}

/** One row in the trace list (`GET /api/traces`) — a trace rolled up to its root + aggregates. */
export interface TraceSummary {
  traceId: string;
  rootName: string;
  rootService: string;
  ts: number; // trace start, epoch ms
  startNs: number; // trace start, unix nanos (µs-precise)
  durationNs: number; // whole-trace wall-clock span
  spanCount: number;
  errorCount: number;
  statusCode: number; // 0 unset, 1 ok, 2 error
  userId: string | null;
  httpMethod: string | null;
  httpRoute: string | null;
  httpStatus: number | null;
}

/** One span in a trace waterfall (`GET /api/traces/:id`). */
export interface TraceSpan {
  traceId: string;
  spanId: string;
  parentSpanId: string | null;
  service: string;
  name: string;
  kind: number;
  startNs: number;
  endNs: number;
  durationNs: number;
  ts: number;
  statusCode: number;
  statusMessage: string | null;
  userId: string | null;
  httpMethod: string | null;
  httpRoute: string | null;
  httpStatus: number | null;
  attrs: Record<string, unknown> | null;
}

export interface TraceListResult {
  traces: TraceSummary[];
  services: string[];
}

export interface TraceDetail {
  traceId: string;
  spans: TraceSpan[];
}

export interface TraceLogsResult {
  traceId: string;
  window: { startMs: number; endMs: number };
  logs: LogRow[];
  hasMore: boolean;
}

export interface TraceListParams {
  from?: number;
  to?: number;
  service?: string;
  userId?: string;
  errorsOnly?: boolean;
  minDurationMs?: number;
  search?: string;
  limit?: number;
}

export interface ContainerInfo {
  container: string;
  stack: string | null;
  service: string | null;
  count24h: number;
  lastTs: number | null;
  running: boolean;
}

export interface FilterSet {
  id: number;
  name: string;
  query: string;
  createdBy: string;
  updatedAt: number;
}

export interface AttrKeyInfo {
  key: string;
  count: number;
  sample: string;
}

// --- Metrics (§2 contract) --------------------------------------------------

/** One catalog row (`GET /api/metrics/catalog`) — one distinct (source,target,name)
 * with its label keys aggregated + a bounded sparkline/lastValue/deltaPct over the
 * window. `description` is a Prometheus HELP / curated friendly name. */
export interface CatalogMetric {
  name: string;
  source: string;
  target: string;
  unit: string | null;
  type: string | null;
  labelKeys: string[];
  description?: string;
  help?: string;
  lastValue: number | null;
  sparkline: number[] | null;
  deltaPct: number | null;
}

export interface MetricsCatalog {
  metrics: CatalogMetric[];
}

export type MetricMatcherOp = 'eq' | 'neq' | 're' | 'nre';
export interface MetricMatcher {
  label: string;
  op: MetricMatcherOp;
  value: string;
}

export type MetricAgg =
  | 'avg'
  | 'sum'
  | 'min'
  | 'max'
  | 'last'
  | 'count'
  | 'rate'
  | 'p50'
  | 'p90'
  | 'p95'
  | 'p99';

/** `POST /api/metrics/query` body (§2.3). */
export interface MetricRangeQuery {
  source?: string;
  name: string;
  target?: string;
  matchers?: MetricMatcher[];
  from: number;
  to: number;
  step: number;
  agg: MetricAgg;
  groupBy?: string[];
}

export interface MetricSeries {
  labels: Record<string, string>;
  points: [number, number][];
}

export interface MetricRangeResult {
  series: MetricSeries[];
  truncated: boolean;
}

/** `POST /api/metrics/instant` body (§2.4). */
export interface MetricInstantQuery {
  source?: string;
  name: string;
  target?: string;
  matchers?: MetricMatcher[];
  from?: number;
  to: number;
  evalWindow: number;
  agg: MetricAgg;
  groupBy?: string[];
}

export interface MetricInstantResult {
  results: Array<{ labels: Record<string, string>; value: number }>;
}

export interface MetricTarget {
  name: string;
  url?: string;
}

/**
 * A saved metric exploration (amendments §3). `spec` is the full serialized
 * chart definition (an `ExplorationSpec` from lib/metric-exploration). The server
 * stores it as JSON; older/edge responses may hand it back as a JSON string, so
 * `explorations.list()` normalizes to a parsed object.
 */
export interface Exploration<Spec = unknown> {
  id: number;
  name: string;
  spec: Spec;
  createdBy: string;
  updatedAt: number;
}

// --- Dashboards (appmodels §3) ------------------------------------------------

/** The shared time-range enum (mirrors useFilters TIME_RANGES; declared here so
 * api.ts stays dependency-free of the composables). */
export type DashboardTimeRange = '15m' | '1h' | '6h' | '24h' | '7d';

export type TileType = 'metric' | 'logs';
/** Dashboard metric-tile render kinds (§3.2) — `stat` is the single-value tile. */
export type MetricTileViz = 'line' | 'area' | 'bar' | 'stat';

export interface MetricInlineSource {
  kind: 'inline';
  metric: string;
  labels?: Record<string, string>;
  agg: MetricAgg;
}
export interface MetricSavedSource {
  kind: 'saved';
  explorationId: number;
}
export interface MetricTileConfig {
  source: MetricInlineSource | MetricSavedSource;
  /** NO time window here — it comes from the dashboard clock (§3.4). */
  viz: MetricTileViz;
}

export interface LogsInlineSource {
  kind: 'inline';
  /** A filter query string (the same URL shape `filtersToQuery` emits). */
  query: string;
}
export interface LogsSavedSource {
  kind: 'saved';
  filterSetId: number;
}
export interface LogsTileConfig {
  source: LogsInlineSource | LogsSavedSource;
  limit?: number;
}

/** One dashboard tile (§3.2). `x/y/w/h` are grid units on a 12-col grid; the
 * v1 UI keeps tiles stacked in array order (y = index, x = 0). */
export interface Tile {
  id: string;
  type: TileType;
  x: number;
  y: number;
  w: number;
  h: number;
  title?: string;
  config: MetricTileConfig | LogsTileConfig;
}

export interface DashboardLayout {
  tiles: Tile[];
}

/** `GET /api/dashboards/:id/resolved` — a saved metric ref dereffed to its
 * exploration spec. `spec` absent + `unresolved` on the config = deleted ref. */
export interface ResolvedMetricSource {
  kind: 'inline';
  explorationId: number;
  /** The exploration's stored chart spec (an ExplorationSpec), verbatim. */
  spec?: unknown;
}
export interface ResolvedLogsSource {
  kind: 'inline';
  filterSetId: number;
  /** The filter set's saved query string. Absent when the set was deleted. */
  query?: string;
}
export interface ResolvedMetricTileConfig {
  source: MetricInlineSource | ResolvedMetricSource;
  viz: MetricTileViz;
  unresolved?: true;
}
export interface ResolvedLogsTileConfig {
  source: LogsInlineSource | ResolvedLogsSource;
  limit?: number;
  unresolved?: true;
}
export interface ResolvedTile extends Omit<Tile, 'config'> {
  config: ResolvedMetricTileConfig | ResolvedLogsTileConfig;
}

export interface DashboardSummary {
  id: number;
  name: string;
  owner: string;
  timeDefault: DashboardTimeRange;
  updatedAt: number;
}

export interface Dashboard extends DashboardSummary {
  layout: DashboardLayout;
}

export interface ResolvedDashboard extends DashboardSummary {
  layout: { tiles: ResolvedTile[] };
}

/** POST/PUT /api/dashboards body (client shape; the wire key is `time_default`). */
export interface DashboardWrite {
  name: string;
  layout: DashboardLayout;
  timeDefault: DashboardTimeRange;
}

/** Snake_case wire rows (§3.3 arch table) → camelCase client shape. */
interface DashboardWireSummary {
  id: number;
  name: string;
  owner: string;
  time_default: DashboardTimeRange;
  updated_at: number;
}

function fromWireSummary(row: DashboardWireSummary): DashboardSummary {
  return {
    id: row.id,
    name: row.name,
    owner: row.owner,
    timeDefault: row.time_default,
    updatedAt: row.updated_at,
  };
}

// --- Alerts (appmodels §4) ------------------------------------------------

export type AlertState = 'ok' | 'pending' | 'firing';
export type AlertOperator = '>' | '>=' | '<' | '<=' | '==' | '!=';

/**
 * One alert row from `GET /api/alerts` / `GET /api/alerts/:id`.
 *
 * SECURITY: the per-alert webhook URL is a secret from NON-ADMIN readers — their
 * reads never include it, only the `has_webhook_override` boolean. ADMIN reads
 * additionally carry `webhook_url` (string, or null when no override), so the
 * admin-only edit form can prefill it.
 */
export interface Alert {
  id: number;
  name: string;
  enabled: boolean;
  metric: string;
  labels: Record<string, string>;
  agg: MetricAgg;
  operator: AlertOperator;
  threshold: number;
  /** Aggregate the metric over the last `evalWindowMs` ms (amendments §8). */
  evalWindowMs: number;
  /** The condition must hold continuously this long before firing (distinct knob, §8). */
  forMs: number;
  /** Minimum gap between repeat notifications while continuously firing. */
  cooldownMs: number;
  /** True when a per-alert webhook override is set (present for every reader). */
  hasWebhookOverride: boolean;
  /** The override URL itself — only present on ADMIN reads (undefined for everyone
   * else); null on an admin read means "no override". */
  webhookUrl?: string | null;
  messageTemplate: string | null;
  notifyOnResolve: boolean;
  state: AlertState;
  stateSince: number | null;
  lastNotifiedAt: number | null;
  lastValue: number | null;
  lastWebhookAt: number | null;
  lastWebhookError: string | null;
  createdAt: number;
  updatedAt: number;
}

/** POST/PUT /api/alerts body (client shape; the wire is snake_case).
 * `webhookUrl` is tri-state on the wire: `undefined` OMITS the key, which the
 * server PUT treats as "keep the stored webhook_url unchanged" (keep-if-absent —
 * this is how a partial edit avoids re-sending the secret); `null` explicitly
 * clears the override (use the org default webhook); a string sets it. Every
 * other field is a full replace. */
export interface AlertInput {
  name: string;
  metric: string;
  labels: Record<string, string>;
  agg: MetricAgg;
  operator: AlertOperator;
  threshold: number;
  evalWindowMs: number;
  forMs: number;
  cooldownMs: number;
  /** undefined = keep the stored value (omit on the wire); null = clear; string = set. */
  webhookUrl: string | null | undefined;
  messageTemplate: string | null;
  notifyOnResolve: boolean;
  enabled: boolean;
}

/** One firing episode from `GET /api/alerts/:id/events` (newest first). */
export interface AlertEvent {
  id: number;
  alertId: number;
  firedAt: number;
  resolvedAt: number | null;
  value: number | null;
  message: string | null;
}

/** `POST /api/alerts/:id/test` result — `status` is the Discord HTTP status,
 * or null when the request never completed (network error / timeout). */
export interface AlertTestResult {
  ok: boolean;
  status: number | null;
}

/** Snake_case wire row (§4.1 column names) → camelCase client shape. */
interface AlertWire {
  id: number;
  name: string;
  enabled: boolean;
  metric: string;
  labels: Record<string, string>;
  agg: MetricAgg;
  operator: AlertOperator;
  threshold: number;
  eval_window_ms: number;
  for_ms: number;
  cooldown_ms: number;
  has_webhook_override: boolean;
  /** Only present on admin reads; null there means "no override". */
  webhook_url?: string | null;
  message_template: string | null;
  notify_on_resolve: boolean;
  state: AlertState;
  state_since: number | null;
  last_notified_at: number | null;
  last_value: number | null;
  last_webhook_at: number | null;
  last_webhook_error: string | null;
  created_at: number;
  updated_at: number;
}

function fromAlertWire(row: AlertWire): Alert {
  return {
    id: row.id,
    name: row.name,
    enabled: row.enabled,
    metric: row.metric,
    labels: row.labels ?? {},
    agg: row.agg,
    operator: row.operator,
    threshold: row.threshold,
    evalWindowMs: row.eval_window_ms,
    forMs: row.for_ms,
    cooldownMs: row.cooldown_ms,
    hasWebhookOverride: row.has_webhook_override === true,
    // Absent (non-admin read) stays undefined — distinct from an admin read's explicit null.
    webhookUrl: row.webhook_url,
    messageTemplate: row.message_template,
    notifyOnResolve: row.notify_on_resolve,
    state: row.state,
    stateSince: row.state_since,
    lastNotifiedAt: row.last_notified_at,
    lastValue: row.last_value,
    lastWebhookAt: row.last_webhook_at,
    lastWebhookError: row.last_webhook_error,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function toAlertWire(input: AlertInput): Record<string, unknown> {
  return {
    name: input.name,
    metric: input.metric,
    labels: input.labels,
    agg: input.agg,
    operator: input.operator,
    threshold: input.threshold,
    eval_window_ms: input.evalWindowMs,
    for_ms: input.forMs,
    cooldown_ms: input.cooldownMs,
    // Tri-state: undefined OMITS the key (server keeps the stored value — the
    // keep-if-absent partial-update path), null sends an explicit clear, a
    // string sets it. `undefined` must map to a truly absent key, not
    // `webhook_url: undefined` — JSON.stringify drops undefined values, but
    // being explicit here keeps the intent readable.
    ...(input.webhookUrl !== undefined ? { webhook_url: input.webhookUrl } : {}),
    ...(input.messageTemplate ? { message_template: input.messageTemplate } : {}),
    notify_on_resolve: input.notifyOnResolve,
    enabled: input.enabled,
  };
}

interface AlertEventWire {
  id: number;
  alert_id: number;
  fired_at: number;
  resolved_at: number | null;
  value: number | null;
  message: string | null;
}

/** The signed-in user's identity + role, from `GET /api/me`. `isAdmin` gates all
 * admin-only UI; it's read fresh from the server (the JWT `admin` claim is only a
 * hint) and treated as false unless the server explicitly says `true`. */
export interface Me {
  username: string;
  isAdmin: boolean;
}

/** A managed account row from `GET /api/users` (admin-only). */
export interface UserRow {
  username: string;
  isAdmin: boolean;
  createdAt: number;
  updatedAt: number;
}

export class ApiError extends Error {
  status: number;
  unauthorized: boolean;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.unauthorized = status === 401;
  }
}

/** Parse JSON that *should* be an object, returning `{}` rather than throwing on
 * a malformed string — used to defensively normalize a string-encoded `spec`. */
function safeParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return {};
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
      ...init,
    });
  } catch (e) {
    throw new ApiError(0, e instanceof Error ? e.message : 'Network error');
  }
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json();
      if (body && typeof body.error === 'string') message = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface QueryParams extends Record<string, string | undefined> {
  /** v0.3 wire format: URL-encoded JSON array of `{value, negate}` text terms. */
  text?: string;
  /** v0.3 wire format: URL-encoded JSON array of `{key, value, negate}` attr terms. */
  attrs?: string;
  containers?: string;
  stacks?: string;
  levels?: string;
  streams?: string;
  from?: string;
  to?: string;
  limit?: string;
  beforeTs?: string;
  beforeId?: string;
}

export const api = {
  async me(): Promise<Me> {
    const res = await request<{ username: string; isAdmin?: boolean }>('/api/me');
    // `isAdmin` is optional on the wire (older servers omit it) — default to
    // false so admin UI stays hidden until the server explicitly grants it.
    return { username: res.username, isAdmin: res.isAdmin === true };
  },

  login(username: string, password: string): Promise<{ token: string }> {
    return request('/api/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },

  logout(): Promise<void> {
    return request('/api/logout', { method: 'POST' });
  },

  query(params: QueryParams): Promise<QueryResult> {
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') search.set(k, v);
    }
    const qs = search.toString();
    return request(`/api/query${qs ? `?${qs}` : ''}`);
  },

  containers(): Promise<ContainerInfo[]> {
    return request('/api/containers');
  },

  changePassword(current: string, newPassword: string): Promise<void> {
    return request('/api/me/password', {
      method: 'POST',
      body: JSON.stringify({ current, new: newPassword }),
    });
  },

  async getUserSetting<T>(key: string): Promise<T | null> {
    const res = await request<{ value: T | null }>(`/api/settings/user/${encodeURIComponent(key)}`);
    return res.value;
  },

  putUserSetting(key: string, value: unknown): Promise<void> {
    return request(`/api/settings/user/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  },

  async getFavoriteFilterSets(): Promise<number[]> {
    const value = await api.getUserSetting<number[]>('favoriteFilterSets');
    return Array.isArray(value) ? value : [];
  },

  putFavoriteFilterSets(ids: number[]): Promise<void> {
    return api.putUserSetting('favoriteFilterSets', ids);
  },

  filterSets: {
    list(): Promise<FilterSet[]> {
      return request('/api/filter-sets');
    },
    save(name: string, query: string): Promise<{ id: number }> {
      return request('/api/filter-sets', {
        method: 'POST',
        body: JSON.stringify({ name, query }),
      });
    },
    delete(id: number): Promise<void> {
      return request(`/api/filter-sets/${id}`, { method: 'DELETE' });
    },
  },

  /** Metrics query API (§2). All read-only + cookie-authed. */
  metrics: {
    catalog(params: { from: number; to: number; search?: string }): Promise<MetricsCatalog> {
      const search = new URLSearchParams();
      search.set('from', String(params.from));
      search.set('to', String(params.to));
      if (params.search) search.set('search', params.search);
      return request(`/api/metrics/catalog?${search.toString()}`);
    },
    query(body: MetricRangeQuery): Promise<MetricRangeResult> {
      return request('/api/metrics/query', { method: 'POST', body: JSON.stringify(body) });
    },
    instant(body: MetricInstantQuery): Promise<MetricInstantResult> {
      return request('/api/metrics/instant', { method: 'POST', body: JSON.stringify(body) });
    },
    targets(): Promise<{ targets: MetricTarget[] }> {
      return request('/api/metrics/targets');
    },
  },

  /** Traces (§6). List rolled-up traces, fetch a waterfall, and pull a trace's correlated logs. */
  traces: {
    list(params: TraceListParams = {}): Promise<TraceListResult> {
      const q = new URLSearchParams();
      if (params.from !== undefined) q.set('from', String(params.from));
      if (params.to !== undefined) q.set('to', String(params.to));
      if (params.service) q.set('service', params.service);
      if (params.userId) q.set('userId', params.userId);
      if (params.errorsOnly) q.set('errorsOnly', 'true');
      if (params.minDurationMs !== undefined) q.set('minDurationMs', String(params.minDurationMs));
      if (params.search) q.set('search', params.search);
      if (params.limit !== undefined) q.set('limit', String(params.limit));
      const qs = q.toString();
      return request(`/api/traces${qs ? `?${qs}` : ''}`);
    },
    get(traceId: string): Promise<TraceDetail> {
      return request(`/api/traces/${encodeURIComponent(traceId)}`);
    },
    logs(traceId: string): Promise<TraceLogsResult> {
      return request(`/api/traces/${encodeURIComponent(traceId)}/logs`);
    },
  },

  /**
   * Saved metric explorations (amendments §3). Mirrors `filterSets` exactly:
   * upsert-by-name on POST (stable id), DELETE by id. NOTE: the backend endpoint
   * is being built to the §3 contract — this client targets that shape and
   * integrates when it lands (a 404 today surfaces as a normal `ApiError`).
   */
  explorations: {
    async list(): Promise<Exploration[]> {
      const rows = await request<Exploration[]>('/api/explorations');
      // Normalize a JSON-string `spec` (defensive — server should send an object).
      return rows.map((r) => ({
        ...r,
        spec: typeof r.spec === 'string' ? safeParse(r.spec) : r.spec,
      }));
    },
    save(name: string, spec: unknown): Promise<{ id: number }> {
      return request('/api/explorations', {
        method: 'POST',
        body: JSON.stringify({ name, spec }),
      });
    },
    delete(id: number): Promise<void> {
      return request(`/api/explorations/${id}`, { method: 'DELETE' });
    },
  },

  /**
   * Dashboards (§3.3). Org-shared, like filter sets/explorations. The wire
   * format is snake_case (`time_default`, `updated_at`) per the arch doc;
   * this client normalizes to camelCase. `/resolved` dereferences saved
   * exploration/filter-set refs to inline specs/queries but NEVER executes
   * tile queries — the view fires those itself with the dashboard clock.
   */
  dashboards: {
    async list(): Promise<DashboardSummary[]> {
      const rows = await request<DashboardWireSummary[]>('/api/dashboards');
      return rows.map(fromWireSummary);
    },
    async get(id: number): Promise<Dashboard> {
      const row = await request<DashboardWireSummary & { layout: DashboardLayout }>(
        `/api/dashboards/${id}`,
      );
      return { ...fromWireSummary(row), layout: row.layout };
    },
    async resolved(id: number): Promise<ResolvedDashboard> {
      const row = await request<DashboardWireSummary & { layout: { tiles: ResolvedTile[] } }>(
        `/api/dashboards/${id}/resolved`,
      );
      return { ...fromWireSummary(row), layout: row.layout };
    },
    create(body: DashboardWrite): Promise<{ id: number }> {
      return request('/api/dashboards', {
        method: 'POST',
        body: JSON.stringify({
          name: body.name,
          layout: body.layout,
          time_default: body.timeDefault,
        }),
      });
    },
    update(id: number, body: DashboardWrite): Promise<void> {
      return request(`/api/dashboards/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: body.name,
          layout: body.layout,
          time_default: body.timeDefault,
        }),
      });
    },
    delete(id: number): Promise<void> {
      return request(`/api/dashboards/${id}`, { method: 'DELETE' });
    },
  },

  /**
   * Alerts (appmodels §4.4). Reads are all-users; create/edit/delete/test are
   * admin-only (403 `ApiError` otherwise — the UI also hides those controls).
   * Webhook URLs are write-only secrets: reads return `hasWebhookOverride` /
   * `configured` booleans, never the URL.
   */
  alerts: {
    async list(): Promise<Alert[]> {
      const rows = await request<AlertWire[]>('/api/alerts');
      return rows.map(fromAlertWire);
    },
    async get(id: number): Promise<Alert> {
      return fromAlertWire(await request<AlertWire>(`/api/alerts/${id}`));
    },
    create(input: AlertInput): Promise<{ id: number }> {
      return request('/api/alerts', { method: 'POST', body: JSON.stringify(toAlertWire(input)) });
    },
    update(id: number, input: AlertInput): Promise<void> {
      return request(`/api/alerts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(toAlertWire(input)),
      });
    },
    delete(id: number): Promise<void> {
      return request(`/api/alerts/${id}`, { method: 'DELETE' });
    },
    /** Sends a SAMPLE webhook using the alert's saved config — no state change,
     * no event row. The "does my webhook actually reach Discord" check. */
    test(id: number): Promise<AlertTestResult> {
      return request(`/api/alerts/${id}/test`, { method: 'POST' });
    },
    async events(id: number, limit?: number): Promise<AlertEvent[]> {
      const qs = limit !== undefined ? `?limit=${String(limit)}` : '';
      const res = await request<{ events: AlertEventWire[] }>(`/api/alerts/${id}/events${qs}`);
      return res.events.map((e) => ({
        id: e.id,
        alertId: e.alert_id,
        firedAt: e.fired_at,
        resolvedAt: e.resolved_at,
        value: e.value,
        message: e.message,
      }));
    },
    /** Org-default Discord webhook — the fallback for alerts with no override.
     * Read returns only whether one is configured (the URL is never echoed). */
    webhookDefault(): Promise<{ configured: boolean }> {
      return request('/api/alerts/webhook-default');
    },
    /** Set (a Discord webhook URL) or clear (null/empty) the org default. Admin-only. */
    setWebhookDefault(value: string | null): Promise<void> {
      return request('/api/alerts/webhook-default', {
        method: 'PUT',
        body: JSON.stringify({ value }),
      });
    },
  },

  /** The login-redirect target (§3.4). Backend prunes a dangling id on read. */
  async getHomeDashboardId(): Promise<number | null> {
    const value = await api.getUserSetting<unknown>('homeDashboardId');
    return typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : null;
  },

  putHomeDashboardId(id: number | null): Promise<void> {
    return api.putUserSetting('homeDashboardId', id);
  },

  async getFavoriteExplorations(): Promise<number[]> {
    const value = await api.getUserSetting<number[]>('favoriteExplorations');
    return Array.isArray(value) ? value : [];
  },

  putFavoriteExplorations(ids: number[]): Promise<void> {
    return api.putUserSetting('favoriteExplorations', ids);
  },

  async getContainerAttrs(): Promise<Record<string, string[]>> {
    const res = await request<{ value: Record<string, string[]> | null }>(
      '/api/settings/org/containerAttrs',
    );
    return res.value ?? {};
  },

  /** Atomic per-container write (`PUT /api/settings/org/containerAttrs/:container`) — used for
   * pinning so two users toggling attrs on two different containers can't clobber each other via
   * a stale full-object PUT. */
  putContainerAttrKeys(container: string, keys: string[]): Promise<void> {
    return request(`/api/settings/org/containerAttrs/${encodeURIComponent(container)}`, {
      method: 'PUT',
      body: JSON.stringify({ keys }),
    });
  },

  attrKeys(params: {
    containers?: string;
    stacks?: string;
    from: string;
    to: string;
  }): Promise<{ keys: AttrKeyInfo[] }> {
    const search = new URLSearchParams();
    if (params.containers) search.set('containers', params.containers);
    if (params.stacks) search.set('stacks', params.stacks);
    search.set('from', params.from);
    search.set('to', params.to);
    return request(`/api/attr-keys?${search.toString()}`);
  },

  /** Distinct attr VALUES for a given key, scoped like `attrKeys` — powers the
   * attr-value autocomplete. `truncated` true = the value space is large and the
   * returned list is only the top-N most common (still useful as suggestions). */
  attrValues(
    key: string,
    params: { containers?: string; stacks?: string; from: string; to: string },
  ): Promise<{ values: string[]; truncated: boolean }> {
    const search = new URLSearchParams();
    search.set('key', key);
    if (params.containers) search.set('containers', params.containers);
    if (params.stacks) search.set('stacks', params.stacks);
    search.set('from', params.from);
    search.set('to', params.to);
    return request(`/api/attr-values?${search.toString()}`);
  },

  /** Admin-only user management (`requireAdmin` server-side). All calls 403 for
   * non-admins and the API key; the UI additionally hides these behind `me.isAdmin`.
   * Guard failures (last admin / self-lockout) come back as 409 `ApiError`s. */
  users: {
    list(): Promise<UserRow[]> {
      return request('/api/users');
    },
    create(username: string, password: string, admin = false): Promise<{ username: string }> {
      return request('/api/users', {
        method: 'POST',
        body: JSON.stringify({ username, password, admin }),
      });
    },
    delete(username: string): Promise<void> {
      return request(`/api/users/${encodeURIComponent(username)}`, { method: 'DELETE' });
    },
    setPassword(username: string, newPassword: string): Promise<void> {
      return request(`/api/users/${encodeURIComponent(username)}/password`, {
        method: 'POST',
        body: JSON.stringify({ new: newPassword }),
      });
    },
    setAdmin(username: string, admin: boolean): Promise<void> {
      return request(`/api/users/${encodeURIComponent(username)}/admin`, {
        method: 'POST',
        body: JSON.stringify({ admin }),
      });
    },
  },
};
